import React, { useState, useEffect } from 'react';
import pb from '../lib/pocketbase';
import { Save, Upload, AlertCircle, Phone, MapPin, Mail, Clock, CalendarDays, Store, Link as LinkIcon, Settings } from 'lucide-react';
import './AdminCrud.css';

const AdminSettings = () => {
    const [activeTab, setActiveTab] = useState('general'); // 'general', 'contact', 'horaires'

    // Core state mapped to PocketBase fields
    const [settings, setSettings] = useState({
        id: null,
        siteName: '',
        siteSlogan: '',
        siteLogo: null,
        phone: '',
        email: '',
        address: '',
        googleMapsUrl: '',
        openingHours: {
            lundi: { ouvert: false, matin: '', apresMidi: '' },
            mardi: { ouvert: false, matin: '', apresMidi: '' },
            mercredi: { ouvert: false, matin: '', apresMidi: '' },
            jeudi: { ouvert: true, matin: '09:00 - 13:00', apresMidi: '14:00 - 18:30' },
            vendredi: { ouvert: true, matin: '09:00 - 13:00', apresMidi: '14:00 - 18:30' },
            samedi: { ouvert: true, matin: '09:00 - 13:00', apresMidi: '14:00 - 18:30' },
            dimanche: { ouvert: true, matin: '09:00 - 13:00', apresMidi: '14:00 - 17:00' }
        },
        clickCollectActive: true,
        heroImage: null,
        specialAlert: '',
        vacationStart: '',
        vacationEnd: '',
        vacationMessage: ''
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Previews for images
    const [logoPreview, setLogoPreview] = useState(null);
    const [heroPreview, setHeroPreview] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [heroFile, setHeroFile] = useState(null);

    const daysOfWeek = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const records = await pb.collection('settings').getList(1, 1);
            if (records.items.length > 0) {
                const record = records.items[0];

                // Format dates for input type="date"
                const formatForDateInput = (dateStr) => {
                    if (!dateStr) return '';
                    return new Date(dateStr).toISOString().split('T')[0];
                };

                setSettings({
                    id: record.id,
                    siteName: record.siteName || '',
                    siteSlogan: record.siteSlogan || '',
                    siteLogo: record.siteLogo,
                    phone: record.phone || '',
                    email: record.email || '',
                    address: record.address || '',
                    googleMapsUrl: record.googleMapsUrl || '',
                    openingHours: record.openingHours || settings.openingHours,
                    clickCollectActive: record.clickCollectActive !== undefined ? record.clickCollectActive : true,
                    heroImage: record.heroImage,
                    specialAlert: record.specialAlert || '',
                    vacationStart: formatForDateInput(record.vacationStart),
                    vacationEnd: formatForDateInput(record.vacationEnd),
                    vacationMessage: record.vacationMessage || ''
                });

                if (record.heroImage) setHeroPreview(pb.files.getURL(record, record.heroImage));
                if (record.siteLogo) setLogoPreview(pb.files.getURL(record, record.siteLogo));
            }
        } catch (err) {
            console.error("Error fetching settings:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (name === 'clickCollectActive' ? checked : value) : value
        }));
    };

    const handleHourChange = (day, field, value) => {
        setSettings(prev => ({
            ...prev,
            openingHours: {
                ...prev.openingHours,
                [day]: {
                    ...prev.openingHours[day],
                    [field]: field === 'ouvert' ? !prev.openingHours[day].ouvert : value
                }
            }
        }));
    };

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            if (type === 'logo') {
                setLogoFile(file);
                setLogoPreview(reader.result);
            } else if (type === 'hero') {
                setHeroFile(file);
                setHeroPreview(reader.result);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            const formData = new FormData();

            // Append basic text flags
            formData.append('siteName', settings.siteName);
            formData.append('siteSlogan', settings.siteSlogan);
            formData.append('phone', settings.phone);
            formData.append('email', settings.email);
            formData.append('address', settings.address);
            formData.append('googleMapsUrl', settings.googleMapsUrl);
            formData.append('clickCollectActive', settings.clickCollectActive);
            formData.append('specialAlert', settings.specialAlert);
            formData.append('vacationMessage', settings.vacationMessage);

            // Append dates
            if (settings.vacationStart) formData.append('vacationStart', new Date(settings.vacationStart).toISOString());
            else formData.append('vacationStart', ''); // Clear if empty

            if (settings.vacationEnd) formData.append('vacationEnd', new Date(settings.vacationEnd).toISOString());
            else formData.append('vacationEnd', '');

            // Ensure valid JSON format for hours
            formData.append('openingHours', JSON.stringify(settings.openingHours));

            // Append files if they changed
            if (logoFile) formData.append('siteLogo', logoFile);
            if (heroFile) formData.append('heroImage', heroFile);

            let record;
            if (settings.id) {
                record = await pb.collection('settings').update(settings.id, formData);
            } else {
                record = await pb.collection('settings').create(formData);
            }

            // Clean up states
            setSettings(prev => ({ ...prev, id: record.id }));
            setLogoFile(null);
            setHeroFile(null);

            alert("✅ Paramètres mis à jour avec succès ! (Rafraîchissez le site côté client pour voir les changements)");

        } catch (err) {
            console.error("Error saving settings:", err);
            alert("Erreur lors de la sauvegarde. Vérifiez les formats.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div style={{ padding: '20px' }}>Chargement du Cockpit...</div>;

    return (
        <div className="admin-crud-container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <h2>Cockpit de Gestion</h2>
            </div>

            {/* Tabs Navigation */}
            <div className="admin-menu-tabs" style={{ display: 'flex', gap: '10px', marginBottom: '25px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
                <button
                    className={`btn ${activeTab === 'general' ? '' : 'btn-outline'}`}
                    onClick={() => setActiveTab('general')}
                    style={activeTab === 'general' ? { background: 'var(--color-brown)', color: 'white', border: '1px solid var(--color-brown)' } : { border: '1px solid var(--color-brown)', color: 'var(--color-brown)', background: 'transparent' }}
                >
                    <Settings size={18} style={{ marginRight: '8px' }} /> Général & Identité
                </button>
                <button
                    className={`btn ${activeTab === 'contact' ? '' : 'btn-outline'}`}
                    onClick={() => setActiveTab('contact')}
                    style={activeTab === 'contact' ? { background: 'var(--color-brown)', color: 'white', border: '1px solid var(--color-brown)' } : { border: '1px solid var(--color-brown)', color: 'var(--color-brown)', background: 'transparent' }}
                >
                    <Phone size={18} style={{ marginRight: '8px' }} /> Contact & Carte
                </button>
                <button
                    className={`btn ${activeTab === 'horaires' ? '' : 'btn-outline'}`}
                    onClick={() => setActiveTab('horaires')}
                    style={activeTab === 'horaires' ? { background: 'var(--color-brown)', color: 'white', border: '1px solid var(--color-brown)' } : { border: '1px solid var(--color-brown)', color: 'var(--color-brown)', background: 'transparent' }}
                >
                    <Clock size={18} style={{ marginRight: '8px' }} /> Horaires & Aléas
                </button>
            </div>

            <form onSubmit={handleSave} className="settings-form" style={{ background: 'white', padding: '30px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>

                {/* ---------- TAB 1: IDENTITE GENERALE ---------- */}
                {activeTab === 'general' && (
                    <div className="tab-pane">
                        <div className="form-group" style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #eee' }}>
                            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
                                <div>
                                    <span style={{ display: 'block', fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--color-brown)' }}>Click & Collect (Prise de commande)</span>
                                    <span style={{ color: '#666', fontSize: '0.9rem' }}>Suspendre temporairement toutes les commandes en ligne</span>
                                </div>
                                <div className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        name="clickCollectActive"
                                        checked={settings.clickCollectActive}
                                        onChange={handleInputChange}
                                        style={{ transform: 'scale(1.5)', cursor: 'pointer' }}
                                    />
                                </div>
                            </label>
                            {!settings.clickCollectActive && (
                                <div style={{ marginTop: '10px', color: '#e74c3c', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.9rem', padding: '10px', background: '#ffebee', borderRadius: '4px' }}>
                                    <AlertCircle size={16} /> Attention: Votre boutique est actuellement fermée aux commandes !
                                </div>
                            )}
                        </div>

                        <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            <div className="form-group">
                                <label style={{ fontWeight: 'bold', marginBottom: '8px', display: 'block', color: 'var(--color-brown)' }}>
                                    <Store size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '5px' }} />
                                    Nom de l'Etablissement
                                </label>
                                <input type="text" name="siteName" value={settings.siteName} onChange={handleInputChange} placeholder="Ex: Maison Cédric Millet" />
                            </div>
                            <div className="form-group">
                                <label style={{ fontWeight: 'bold', marginBottom: '8px', display: 'block', color: 'var(--color-brown)' }}>Phrase d'accroche (Slogan)</label>
                                <input type="text" name="siteSlogan" value={settings.siteSlogan} onChange={handleInputChange} placeholder="Ex: Pâtisserie Fine & Salon de Thé" />
                            </div>
                        </div>

                        <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '30px' }}>
                            {/* Logo */}
                            <div className="form-group">
                                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '10px' }}>Logo du Site (Menu & Footer)</label>
                                <div style={{ border: '1px dashed #ccc', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
                                    {logoPreview ? (
                                        <img src={logoPreview} alt="Logo" style={{ maxHeight: '100px', marginBottom: '15px' }} />
                                    ) : <div style={{ height: '100px', background: '#f5f5f5', marginBottom: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>Aucun logo uploadé</div>}
                                    <input type="file" id="logo-upload" accept="image/*" onChange={(e) => handleFileChange(e, 'logo')} style={{ display: 'none' }} />
                                    <label htmlFor="logo-upload" className="btn btn-outline" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 15px' }}>
                                        <Upload size={14} /> Choisir
                                    </label>
                                </div>
                            </div>

                            {/* Hero Image */}
                            <div className="form-group">
                                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '10px' }}>Image d'Accueil Principale (Hero)</label>
                                <div style={{ border: '1px dashed #ccc', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
                                    {heroPreview ? (
                                        <img src={heroPreview} alt="Hero" style={{ maxHeight: '100px', width: '100%', objectFit: 'cover', marginBottom: '15px', borderRadius: '4px' }} />
                                    ) : <div style={{ height: '100px', background: '#f5f5f5', marginBottom: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>Aucune image</div>}
                                    <input type="file" id="hero-upload" accept="image/*" onChange={(e) => handleFileChange(e, 'hero')} style={{ display: 'none' }} />
                                    <label htmlFor="hero-upload" className="btn btn-outline" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 15px' }}>
                                        <Upload size={14} /> Choisir (1920x1080px rec.)
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ---------- TAB 2: CONTACT & MAP ---------- */}
                {activeTab === 'contact' && (
                    <div className="tab-pane">
                        <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            <div className="form-group">
                                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}><Phone size={16} style={{ display: 'inline', verticalAlign: 'middle' }} /> Téléphone</label>
                                <input type="text" name="phone" value={settings.phone} onChange={handleInputChange} placeholder="05 67 67 41 29" />
                            </div>
                            <div className="form-group">
                                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}><Mail size={16} style={{ display: 'inline', verticalAlign: 'middle' }} /> Adresse Email</label>
                                <input type="email" name="email" value={settings.email} onChange={handleInputChange} placeholder="contact@boutique.fr" />
                            </div>
                        </div>

                        <div className="form-group" style={{ marginTop: '20px' }}>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}><MapPin size={16} style={{ display: 'inline', verticalAlign: 'middle' }} /> Adresse Postale (Affichée sur le site)</label>
                            <input type="text" name="address" value={settings.address} onChange={handleInputChange} placeholder="102 rue Porta, 81000 ALBI" />
                        </div>

                        <div className="form-group" style={{ marginTop: '20px' }}>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}><LinkIcon size={16} style={{ display: 'inline', verticalAlign: 'middle' }} /> Lien de partage Google Maps</label>
                            <input type="url" name="googleMapsUrl" value={settings.googleMapsUrl} onChange={handleInputChange} placeholder="https://maps.app.goo.gl/..." />
                            <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '5px' }}>Allez sur Google Maps {'>'} Partager {'>'} Copier le lien. Permet de guider les clients.</p>
                        </div>
                    </div>
                )}

                {/* ---------- TAB 3: HEURES & VACANCES ---------- */}
                {activeTab === 'horaires' && (
                    <div className="tab-pane">

                        <div style={{ background: '#fcf8e3', padding: '20px', borderRadius: '8px', marginBottom: '30px', border: '1px solid #faebcc' }}>
                            <h4 style={{ color: '#8a6d3b', marginTop: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <AlertCircle size={18} /> Alertes & Congés Exceptionnels
                            </h4>

                            <div className="form-group" style={{ marginBottom: '15px' }}>
                                <label style={{ fontWeight: 'bold', display: 'block', fontSize: '0.9rem' }}>Bannière d'Alerte (Laissez vide pour cacher)</label>
                                <input type="text" name="specialAlert" value={settings.specialAlert} onChange={handleInputChange} placeholder="Ex: Livraison exceptionnelle en centre-ville ce vendredi !" style={{ borderColor: '#faebcc' }} />
                            </div>

                            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                                <div className="form-group">
                                    <label style={{ fontWeight: 'bold', display: 'block', fontSize: '0.9rem' }}>Début des Vacances</label>
                                    <input type="date" name="vacationStart" value={settings.vacationStart} onChange={handleInputChange} style={{ borderColor: '#faebcc' }} />
                                </div>
                                <div className="form-group">
                                    <label style={{ fontWeight: 'bold', display: 'block', fontSize: '0.9rem' }}>Fin des Vacances</label>
                                    <input type="date" name="vacationEnd" value={settings.vacationEnd} onChange={handleInputChange} style={{ borderColor: '#faebcc' }} />
                                </div>
                            </div>

                            <div className="form-group">
                                <label style={{ fontWeight: 'bold', display: 'block', fontSize: '0.9rem' }}>Message à afficher pendant la fermeture</label>
                                <textarea name="vacationMessage" value={settings.vacationMessage} onChange={handleInputChange} rows="2" placeholder="Ex: Boutique fermée pour congés. Réouverture le..." style={{ borderColor: '#faebcc' }}></textarea>
                            </div>
                        </div>

                        <h3 style={{ borderBottom: '2px solid #eee', paddingBottom: '10px', marginBottom: '20px', color: 'var(--color-brown)' }}>
                            <CalendarDays size={20} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '8px' }} />
                            Disponibilité Hebdomadaire
                        </h3>
                        <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '20px' }}>
                            Définissez vos horaires d'ouverture classiques. Laissez un champ vide si fermé sur cette demi-journée.
                        </p>

                        <div className="hours-grid">
                            <div style={{ display: 'grid', gridTemplateColumns: '100px 80px 1fr 1fr', fontWeight: 'bold', marginBottom: '10px', color: '#666', fontSize: '0.9rem' }}>
                                <div>Jour</div>
                                <div>Statut</div>
                                <div>Matin (ex: 09:00 - 13:00)</div>
                                <div>Après-Midi (ex: 14:00 - 18:30)</div>
                            </div>

                            {daysOfWeek.map(day => {
                                const dayData = settings.openingHours[day] || { ouvert: false, matin: '', apresMidi: '' };
                                return (
                                    <div key={day} style={{ display: 'grid', gridTemplateColumns: '100px 80px 1fr 1fr', gap: '15px', alignItems: 'center', marginBottom: '10px', background: dayData.ouvert ? 'white' : '#f9f9f9', padding: '8px', borderRadius: '4px', border: '1px solid #eee' }}>
                                        <div style={{ textTransform: 'capitalize', fontWeight: 'bold', color: 'var(--color-brown)' }}>{day}</div>
                                        <div>
                                            <label className="toggle-switch" style={{ margin: 0 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={dayData.ouvert}
                                                    onChange={(e) => handleHourChange(day, 'ouvert', null)}
                                                />
                                            </label>
                                        </div>
                                        <div>
                                            <input
                                                type="text"
                                                value={dayData.matin}
                                                onChange={(e) => handleHourChange(day, 'matin', e.target.value)}
                                                disabled={!dayData.ouvert}
                                                style={{ opacity: dayData.ouvert ? 1 : 0.5 }}
                                            />
                                        </div>
                                        <div>
                                            <input
                                                type="text"
                                                value={dayData.apresMidi}
                                                onChange={(e) => handleHourChange(day, 'apresMidi', e.target.value)}
                                                disabled={!dayData.ouvert}
                                                style={{ opacity: dayData.ouvert ? 1 : 0.5 }}
                                            />
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}

                {/* Save Button (always visible at bottom) */}
                <div style={{ textAlign: 'right', marginTop: '30px', paddingTop: '20px', borderTop: '2px dashed #eee' }}>
                    <button
                        type="submit"
                        className="btn"
                        disabled={saving}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '1.1rem', padding: '12px 25px' }}
                    >
                        <Save size={20} />
                        {saving ? "Sauvegarde..." : "Publier les Configurations"}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AdminSettings;
